import {z} from 'zod';
import {defineToolRegistryManifest} from '../../types/base-tool-types';
import {dataToolSchemas} from '../../utils/schemas';

const toolName = 'publish-cms-collection-items' as const;

const argumentZod = z
  .object({
    collectionId: dataToolSchemas.collectionId.describe(
      'The ID of the collection containing the items to publish'
    ),
    itemIds: z
      .array(z.string())
      .min(1)
      .max(100)
      .optional()
      .describe(
        'Array of item IDs to publish. Used for publishing items with default locale. Either itemIds or items must be provided. When providing only the item ids (no locales), this option is preferred.'
      ),
    items: z
      .array(
        z.object({
          id: z.string().describe('The ID of the item to publish'),
          cmsLocaleIds: z
            .array(z.string())
            .optional()
            .describe('Array of locale IDs for this specific item'),
        })
      )
      .min(1)
      .max(100)
      .optional()
      .describe(
        'Array of items with specific locale settings. Either itemIds or items must be provided. When providing items with locales, this option is preferred.'
      ),
  })
  .refine(
    (data) =>
      (data.itemIds && data.itemIds.length > 0) ||
      (data.items && data.items.length > 0),
    {
      message:
        'Either itemIds or items must be provided with at least one item',
    }
  )
  .refine((data) => !(data.itemIds && data.items), {
    message:
      'Cannot provide both itemIds and items. Use either itemIds for simple publishing or items for locale-specific publishing.',
  });

export const publishCMSCollectionItemsManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Publish CMS collection items',
  family: 'cms',
  llmTool: {
    name: toolName,
    description:
      'Never call this without confirming with the user. Publishes staged CMS collection items to make them live on the website. Can publish items with default locale using itemIds array, or publish items with specific locales using items array. The collectionId must be passed in the params for either option.',
    inputSchema: argumentZod,
  },
  type: 'server',
  confirmBeforeExecuting: true,
});

export type PublishCMSCollectionItemsArgumentType = z.infer<typeof argumentZod>;
